import type { PricingParametersDTO, PricingTemplateDTO } from "./dto.js";

export const PRICING_FIELDS = [
  "costProfitRate", "returnRate", "commissionRate", "advertisingRate", "activityDiscountRate",
  "domesticShippingCost", "otherFees", "shippingMethodId", "defaultWeightKg",
  "wbSourcePriceMultiplier", "wbSourceMinimumPriceEnabled", "wbSourceMinimumPriceCny"
] as const;
export type PricingTemplateSettings = PricingParametersDTO & {
  pricingTemplates: PricingTemplateDTO[];
  defaultPricingTemplateId: string;
};
const shippingMethods = ["325994", "325999", "357322", "382818", "50072222"];

const nameKey = (name: string): string => name.trim().toLowerCase();

export function pricingTemplateNameError(templates: readonly PricingTemplateDTO[], name: string, currentId?: string): string | null {
  if (!name.trim()) return "方案名称不能为空";
  if (name.trim().length > 200) return "方案名称最多 200 字符";
  return templates.some(template => template.id !== currentId && nameKey(template.name) === nameKey(name))
    ? `方案名称「${name.trim()}」已存在，请使用其他名称` : null;
}

export function duplicatePricingTemplateNames(templates: readonly PricingTemplateDTO[]): string[] {
  const seen = new Set<string>(), duplicates = new Map<string, string>();
  for (const template of templates) {
    const key = nameKey(template.name);
    if (seen.has(key)) duplicates.set(key, template.name.trim());
    seen.add(key);
  }
  return [...duplicates.values()];
}

export function assertUniquePricingTemplateNames(templates: readonly PricingTemplateDTO[]): void {
  const duplicates = duplicatePricingTemplateNames(templates);
  if (duplicates.length) throw new Error(`定价方案名称重复：${duplicates.map(name => `「${name}」`).join("、")}，请重命名后再保存`);
}

export function uniquePricingTemplateName(templates: readonly PricingTemplateDTO[], requestedName: string): string {
  const base = requestedName.trim().slice(0, 200);
  const names = new Set(templates.map(template => nameKey(template.name)));
  if (!names.has(nameKey(base))) return base;
  for (let number = 2; ; number += 1) {
    const suffix = ` ${number}`;
    const candidate = base.slice(0, 200 - suffix.length).trimEnd() + suffix;
    if (!names.has(nameKey(candidate))) return candidate;
  }
}

export function pricingTemplateValues(template: PricingParametersDTO): PricingParametersDTO {
  return Object.fromEntries(PRICING_FIELDS.map(key => [key, template[key]])) as PricingParametersDTO;
}

function legacyTemplate(input: Record<string, unknown>): PricingTemplateDTO {
  return {
    id: "default", name: "默认方案",
    costProfitRate: Number(input["costProfitRate"] ?? input["markupRate"] ?? 0.6),
    returnRate: Number(input["returnRate"] ?? 0.25), commissionRate: Number(input["commissionRate"] ?? 0.25),
    advertisingRate: Number(input["advertisingRate"] ?? 0), activityDiscountRate: Number(input["activityDiscountRate"] ?? 0.05),
    domesticShippingCost: Number(input["domesticShippingCost"] ?? 3), otherFees: Number(input["otherFees"] ?? 5),
    shippingMethodId: typeof input["shippingMethodId"] === "string" && shippingMethods.includes(input["shippingMethodId"])
      ? input["shippingMethodId"] : "325999",
    defaultWeightKg: Number(input["defaultWeightKg"] ?? 0.5),
    wbSourcePriceMultiplier: Number(input["wbSourcePriceMultiplier"] ?? 1),
    wbSourceMinimumPriceEnabled: input["wbSourceMinimumPriceEnabled"] === true,
    wbSourceMinimumPriceCny: input["wbSourceMinimumPriceCny"] == null ? null : Number(input["wbSourceMinimumPriceCny"])
  };
}

function validate(template: PricingTemplateDTO): void {
  const rates = [template.returnRate, template.commissionRate, template.advertisingRate, template.activityDiscountRate];
  const nonnegative = [template.costProfitRate, template.domesticShippingCost, template.otherFees];
  if (rates.some(value => typeof value !== "number" || !Number.isFinite(value) || value < 0 || value >= 1)
    || nonnegative.some(value => typeof value !== "number" || !Number.isFinite(value) || value < 0)
    || (1 - template.returnRate) * (1 - template.commissionRate) - template.advertisingRate <= 0
    || !shippingMethods.includes(template.shippingMethodId)
    || typeof template.defaultWeightKg !== "number" || !Number.isFinite(template.defaultWeightKg) || template.defaultWeightKg < 0.001 || template.defaultWeightKg > 1000
    || typeof template.wbSourcePriceMultiplier !== "number" || !Number.isFinite(template.wbSourcePriceMultiplier) || template.wbSourcePriceMultiplier <= 0 || template.wbSourcePriceMultiplier > 100
    || typeof template.wbSourceMinimumPriceEnabled !== "boolean"
    || (template.wbSourceMinimumPriceCny !== null && (typeof template.wbSourceMinimumPriceCny !== "number" || !Number.isFinite(template.wbSourceMinimumPriceCny) || template.wbSourceMinimumPriceCny < 0 || template.wbSourceMinimumPriceCny > 10000000))
    || (template.wbSourceMinimumPriceEnabled && (template.wbSourceMinimumPriceCny === null || template.wbSourceMinimumPriceCny <= 0))) {
    throw new Error(`定价方案「${template.name}」参数无效，请检查费率、运费或最低售价`);
  }
}

export function resolvePricingTemplates(input: Record<string, unknown>): PricingTemplateSettings {
  let templates: PricingTemplateDTO[];
  if (input["pricingTemplates"] === undefined) {
    // Existing settings and jobs need no migration or new required fields.
    templates = [legacyTemplate(input)];
  } else {
    if (!Array.isArray(input["pricingTemplates"]) || !input["pricingTemplates"].length) throw new Error("至少保留一个定价方案");
    const ids = new Set<string>();
    templates = input["pricingTemplates"].map((item: unknown) => {
      if (!item || typeof item !== "object" || Array.isArray(item)) throw new Error("定价方案无效");
      const source = item as Record<string, unknown>;
      if (typeof source["id"] !== "string" || !source["id"].trim() || source["id"].length > 200 || ids.has(source["id"])) throw new Error("定价方案 ID 无效或重复");
      ids.add(source["id"]);
      if (typeof source["name"] !== "string" || !source["name"].trim() || source["name"].length > 200) throw new Error("定价方案名称不能为空且最多 200 字符");
      const template = { id: source["id"], name: source["name"].trim(), ...Object.fromEntries(PRICING_FIELDS.map(key => [key, source[key]])) } as PricingTemplateDTO;
      validate(template);
      return template;
    });
  }
  const defaultId = input["pricingTemplates"] === undefined ? templates[0]!.id : input["defaultPricingTemplateId"];
  const active = templates.find(template => template.id === defaultId);
  if (!active) throw new Error("请选择有效的默认定价方案");
  return { pricingTemplates: templates, defaultPricingTemplateId: active.id, ...pricingTemplateValues(active) };
}

export function mergePricingTemplates(input: Record<string, unknown>, existing: Record<string, unknown>): PricingTemplateSettings {
  if (input["pricingTemplates"] !== undefined) return resolvePricingTemplates(input);
  const current = resolvePricingTemplates(existing);
  const changes = Object.fromEntries(PRICING_FIELDS.filter(key => input[key] !== undefined).map(key => [key, input[key]]));
  if (input["costProfitRate"] === undefined && input["markupRate"] !== undefined) changes["costProfitRate"] = input["markupRate"];
  // Old clients may update their default parameters without deleting other saved templates.
  return resolvePricingTemplates({
    pricingTemplates: current.pricingTemplates.map(template => template.id === current.defaultPricingTemplateId ? { ...template, ...changes } : template),
    defaultPricingTemplateId: current.defaultPricingTemplateId
  });
}
